import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, Button, Card, IconButton, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  enqueueSnackbar,
  FormProvider,
  FullPageSpinner,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { AlertTriangleIcon, BusinessIcon, CashIcon, InvestIcon, OthersAssetsIcon, PensionIcon, PlusIcon, PropertyIcon } from '@zeta-core/assets';
import {
  AIInteractionService,
  IFactFindProcessSectionType,
  IFactFindProcessStatus,
  InteractionSubSectionType,
  InvestorService,
  IPatchAIInteractionSectionRequest,
  QueryAPIs,
} from '@zeta-services';
import {
  FactFindType,
  IAssetPayload,
  IAssetResponseApi,
  IClientCardItem,
  IFactFindEntity,
  IViewFactFindRequest,
  IViewFinancialSnapshotRequest,
  IViewFinancialSnapshotResponse,
} from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { clientPartnerJointTabs, getLabel } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import SaveDialog from '../components/save-dialog';
import SimpleFieldsTable from '../components/simple-fields-table';
import SuccessDialog from '../components/success-dialog';
import SummaryTabComponent from '../components/summary-tab-component';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import {
  assetsDefaultValues,
  assetsValidationSchema,
  BUSINESS_ASSETS_TABLE_STRUCTURE,
  businessAssetsDefault,
  CASH_ASSETS_TABLE_STRUCTURE,
  cashAssetsDefault,
  INVESTMENT_ASSETS_TABLE_STRUCTURE,
  investmentAssetsDefault,
  OTHER_ASSETS_TABLE_STRUCTURE,
  otherAssetsDefault,
  PENSION_ASSETS_TABLE_STRUCTURE,
  pensionAssetsDefault,
  PROPERTY_ASSETS_TABLE_STRUCTURE,
  propertyAssetsDefault,
} from './form-helpers';
import TotalAssetsCard from './total-assets-card';
import { mapAssetsResponseToSchema, mapToPayloadAssets } from './transform';

enum ISubSectionType {
  cash_assets = 'cash_assets',
  investment_assets = 'investment_assets',
  business_assets = 'business_assets',
  pension_assets = 'pension_assets',
  property_assets = 'property_assets',
  other_assets = 'other_assets',
}

export const Assets = ({
  isClient,
  isManagedClient,
  from,
  refetch,
  clientData,
  conflictData,
  clientSummary,
  updatedClientPartnerDetails,
}: {
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);

  const location = useLocation();

  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const {
    data: sourceControlData,
    mutateAsync: sourceControlMutateAsync,
    isPending: sourceControlPending,
    clientPartnerDetails,
  } = useSourceControlContext() || {};

  type tabType = 'client' | 'partner' | 'joint' | 'summary';
  const [currentTab, setCurrentTab] = useState<tabType>('summary');
  const isAllTranscripts = useBoolean();
  const isTranscript = useBoolean();
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);

  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.assets?.subsections);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.asset_details)?.missing_fields;
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === IFactFindProcessSectionType.assets)?.status;

  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);

  const cashAssetAccountTypeOptions = defaultEnums?.cashAssetAccountType?.map((e) => e.cashAssetAccountType) ?? [];
  const investmentAssetAccountTypeOptions = defaultEnums?.investmentAssetAccountType?.map((e) => e.investmentAssetAccountType) ?? [];
  const pensionTypeOptions = defaultEnums?.pensionType?.map((e) => e.pensionType) ?? [];
  const propertyTypeOptions = defaultEnums?.propertyType?.map((e) => e.propertyType) ?? [];

  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  type assetsValidationSchemaType = InferType<typeof assetsValidationSchema>;

  const handleChangeTab = (event: SyntheticEvent, value: tabType) => {
    setCurrentTab(value);
  };

  const handleChangeSection = (event: SyntheticEvent, value: string) => {
    setSelectSubSection(value);
  };

  const methods = useForm<assetsValidationSchemaType>({ defaultValues: assetsDefaultValues, resolver: yupResolver(assetsValidationSchema) });

  const { reset } = methods;

  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [selectedSubSection, setSelectSubSection] = useState<string>(ISubSectionType.cash_assets);
  const isSuccess = useBoolean(false);
  const isError = useBoolean(false);

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const assetFieldArrays = {
    client: {
      cashAssets: useFieldArray({ name: 'client.cashAssets', control: methods.control }),
      investmentAssets: useFieldArray({ name: 'client.investmentAssets', control: methods.control }),
      businessAssets: useFieldArray({ name: 'client.businessAssets', control: methods.control }),
      pensionAssets: useFieldArray({ name: 'client.pensionAssets', control: methods.control }),
      propertyAssets: useFieldArray({ name: 'client.propertyAssets', control: methods.control }),
      otherAssets: useFieldArray({ name: 'client.otherAssets', control: methods.control }),
    },
    partner: {
      cashAssets: useFieldArray({ name: 'partner.cashAssets', control: methods.control }),
      investmentAssets: useFieldArray({ name: 'partner.investmentAssets', control: methods.control }),
      businessAssets: useFieldArray({ name: 'partner.businessAssets', control: methods.control }),
      pensionAssets: useFieldArray({ name: 'partner.pensionAssets', control: methods.control }),
      propertyAssets: useFieldArray({ name: 'partner.propertyAssets', control: methods.control }),
      otherAssets: useFieldArray({ name: 'partner.otherAssets', control: methods.control }),
    },
    joint: {
      cashAssets: useFieldArray({ name: 'joint.cashAssets', control: methods.control }),
      investmentAssets: useFieldArray({ name: 'joint.investmentAssets', control: methods.control }),
      businessAssets: useFieldArray({ name: 'joint.businessAssets', control: methods.control }),
      pensionAssets: useFieldArray({ name: 'joint.pensionAssets', control: methods.control }),
      propertyAssets: useFieldArray({ name: 'joint.propertyAssets', control: methods.control }),
      otherAssets: useFieldArray({ name: 'joint.otherAssets', control: methods.control }),
    },
  };

  const assetConfigs = [
    {
      label: 'Cash Assets',
      fieldName: 'cashAssets',
      tableStructure: CASH_ASSETS_TABLE_STRUCTURE(ffConfig, cashAssetAccountTypeOptions),
      default: cashAssetsDefault,
      show: ffConfig?.cashAssets?.active ?? true,
      key: ISubSectionType.cash_assets,
      icon: CashIcon,
    },
    {
      label: 'Investment Assets',
      fieldName: 'investmentAssets',
      tableStructure: INVESTMENT_ASSETS_TABLE_STRUCTURE(ffConfig, investmentAssetAccountTypeOptions),
      default: investmentAssetsDefault,
      show: ffConfig?.investmentAssets?.active ?? true,
      key: ISubSectionType.investment_assets,
      icon: InvestIcon,
    },
    {
      label: 'Business Assets',
      fieldName: 'businessAssets',
      tableStructure: BUSINESS_ASSETS_TABLE_STRUCTURE(ffConfig),
      default: businessAssetsDefault,
      show: ffConfig?.businessAssets?.active ?? true,
      key: ISubSectionType.business_assets,
      icon: BusinessIcon,
    },
    {
      label: 'Pension Assets',
      fieldName: 'pensionAssets',
      tableStructure: PENSION_ASSETS_TABLE_STRUCTURE(ffConfig, pensionTypeOptions),
      default: pensionAssetsDefault,
      show: ffConfig?.pensionAssets?.active ?? true,
      key: ISubSectionType.pension_assets,
      icon: PensionIcon,
    },
    {
      label: 'Property Assets',
      fieldName: 'propertyAssets',
      tableStructure: PROPERTY_ASSETS_TABLE_STRUCTURE(ffConfig, propertyTypeOptions),
      default: propertyAssetsDefault,
      show: ffConfig?.propertyAssets?.active ?? true,
      key: ISubSectionType.property_assets,
      icon: PropertyIcon,
    },
    {
      label: 'Other Assets',
      fieldName: 'otherAssets',
      tableStructure: OTHER_ASSETS_TABLE_STRUCTURE(ffConfig),
      default: otherAssetsDefault,
      show: ffConfig?.otherAssets?.active ?? true,
      key: ISubSectionType.other_assets,
      icon: OthersAssetsIcon,
    },
  ];

  const [summaryData, setSummaryData] = useState({});
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);

  const calculateSummary = useMutation({
    mutationFn: (payload: IViewFinancialSnapshotRequest) => QueryAPIs().viewFinancialSnapshot(payload),

    onSuccess: (response: AxiosResponse<IViewFinancialSnapshotResponse>) => {
      const { data: responseData } = response;

      const summary = {
        totalClientValue: responseData?.client?.assetAmount,
        totalPartnerValue: responseData?.partner?.assetAmount,
        totalJointValue: responseData?.joint?.assetAmount,
        total: responseData?.all?.assetAmount,
      };

      setSummaryData(summary);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      calculateSummary.mutateAsync({ clientID: state?.clientData?.clientID, partnerID: clientPartnerDetails?.partnerDetail?.id ?? '' });
    }
    if (from === FactFindType.AI) {
      const summary = {
        totalClientValue: clientSummary?.client?.assetAmount,
        totalPartnerValue: clientSummary?.partner?.assetAmount,
        totalJointValue: clientSummary?.joint?.assetAmount,
        total: clientSummary?.all?.assetAmount,
      };
      setSummaryData(summary);
    }
  }, []);

  const fetchFieldArray = (type: string, tab: tabType) => assetFieldArrays[tab][type];

  const viewAssetsApi = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewAssets(payload),

    onSuccess: (data: AxiosResponse<IAssetResponseApi>) => {
      const transformedDataToMapToAssetsSchema = mapAssetsResponseToSchema(data.data.response);
      methods.reset(transformedDataToMapToAssetsSchema);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      viewAssetsApi.mutateAsync({ clientID: state.clientData?.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      const transformedDataToMapToAssetsSchema = mapAssetsResponseToSchema(clientData?.response);
      reset(transformedDataToMapToAssetsSchema);
    }
  }, [reset, from, clientData?.response]);

  const captureAssetsApi = useMutation({
    mutationFn: (payload: IAssetPayload) => InvestorService().captureAssets(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      viewAssetsApi.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      calculateSummary.mutateAsync({ clientID: state?.clientData?.clientID, partnerID: clientPartnerDetails?.partnerDetail?.id ?? '' });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Assets saved successfully', { variant: 'success' });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const captureAssetsApiAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      refetch?.();
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Assets saved successfully', { variant: 'success' });
      updateFFErrorStore({ ffErrors: null });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const onSubmit = (formData: any) => {
    const payload = { clientID: state.clientData.clientID, ...mapToPayloadAssets(formData) };
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureAssetsApi.mutateAsync(payload);
    }
    if (from === FactFindType.AI) {
      const payloadAI = {
        section: InteractionSubSectionType.asset_details,
        client_id: state.clientData.clientID,
        data: { response: { assets: mapToPayloadAssets(formData) } },
      };
      captureAssetsApiAI.mutate(payloadAI);
    }
  };

  const calculateTotalAmount = (assets: any, fieldName: string) => {
    if (fieldName === 'propertyAssets' || fieldName === 'otherAssets') {
      return assets.reduce((total: number, asset: any) => total + (parseFloat(asset.currentValue ?? 0) || 0), 0);
    }

    if (fieldName === 'businessAssets') {
      return assets.reduce((total: number, asset: any) => total + (parseFloat(asset.value ?? 0) || 0), 0);
    }

    if (fieldName === 'pensionAssets') {
      return assets.reduce((total: number, asset: any) => total + parseFloat(asset.cetv ?? 0) + parseFloat(asset.transferValue ?? 0) || 0, 0);
    }

    return assets.reduce((total: number, asset: any) => total + (parseFloat(asset.amount || asset.value) || 0), 0);
  };

  const getClientsOptions = clientPartnerJointTabs(true, true).map((tab) => ({
    ...tab,
    label: getLabel(tab, from === FactFindType.AI || isClient ? updatedClientPartnerDetails : clientPartnerDetails),
  }));

  return (
    <Box>
      {isTranscript.value && <Transcript section="Assets" isTranscript={isTranscript} />}
      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}

      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          {viewAssetsApi.isPending || sourceControlPending ? (
            <FullPageSpinner />
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                <FactFindHeading
                  sectionName="Assets"
                  isAllTranscripts={isAllTranscripts}
                  isTranscript={isTranscript}
                  hideButton={sourceControlData?.dataSourceMap?.Assets === 'ManualInput'}
                  hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
                />

                {conflictData?.length > 0 && (
                  <Tooltip title="Click here to view all the conflicts">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        setConflictsDialog(true);
                      }}
                      sx={{ marginTop: '-12px' }}
                    >
                      <WarningRounded />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Autocomplete
                  id="assets-dropdown"
                  options={
                    from === FactFindType.PARA_PLANNER ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label)) : getClientsOptions
                  }
                  value={getClientsOptions.find((tab) => tab.value === currentTab)?.label ?? ''}
                  sx={{ width: 300, height: '56px' }}
                  onChange={(e, v) => handleChangeTab(e, v?.value)}
                  getOptionLabel={(option) => option?.label || option}
                  renderInput={(params) => <TextField {...params} />}
                  disableClearable={true}
                />
                {currentTab === 'summary' ? null : (
                  <Tabs
                    sx={{
                      borderBottom: '2px solid rgba(3, 5, 7, 0.08)',
                      '& .MuiTabs-scrollButtons': {
                        opacity: 0.3,
                        visibility: 'visible',
                      },
                      '& .MuiTabs-scrollButtons.Mui-disabled': {
                        opacity: 0.3,
                      },
                    }}
                    value={selectedSubSection}
                    onChange={handleChangeSection}
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                  >
                    {assetConfigs.map((section) => {
                      if (!section.show) return null;
                      return (
                        <Tab
                          key={section.key}
                          value={section.key}
                          id={section.label}
                          label={
                            <>
                              {section.label}
                              {section.errors && <AlertTriangleIcon />}
                            </>
                          }
                          iconPosition="start"
                        />
                      );
                    })}
                  </Tabs>
                )}
              </Stack>

              <Stack spacing={1} marginTop={2} marginBottom={20}>
                {currentTab === 'summary' && (
                  <SummaryTabComponent
                    section="Assets"
                    clientPartnerDetails={from === FactFindType.AI ? updatedClientPartnerDetails : clientPartnerDetails}
                    summaryData={summaryData}
                  />
                )}

                {['client', 'partner', 'joint'].map(
                  (tab) =>
                    currentTab === tab && (
                      <Fragment key={tab}>
                        {assetConfigs.map(
                          (config) =>
                            config.show &&
                            selectedSubSection === config.key && (
                              <Fragment key={config.fieldName}>
                                <Stack marginBottom={1.5}>
                                  <Typography variant="h6" id={config.label}>
                                    {config.label}
                                  </Typography>
                                </Stack>

                                {isReadOnly && fetchFieldArray(config.fieldName, tab).fields?.length === 0 && (
                                  <Card>
                                    <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                      <EmptyContent
                                        description={`No ${config.label?.toLowerCase()} information has been captured as part of the fact-finding process.`}
                                      />
                                    </Stack>
                                  </Card>
                                )}

                                {fetchFieldArray(config.fieldName, tab).fields?.length > 0 && (
                                  <SimpleFieldsTable
                                    fieldArray={fetchFieldArray(config.fieldName, tab).fields}
                                    currentTab={`${currentTab}.${config.fieldName}`}
                                    onRemove={fetchFieldArray(config.fieldName, tab).remove}
                                    tableDetails={config.tableStructure}
                                    isClient={isClient}
                                    isReadOnly={isReadOnly}
                                  />
                                )}
                                {!isClient && !isReadOnly ? (
                                  <Stack flexDirection="row" marginTop={1}>
                                    <Button
                                      variant="text"
                                      color="primary"
                                      id={`Add ${config.label}`}
                                      size="small"
                                      startIcon={<PlusIcon />}
                                      onClick={() => fetchFieldArray(config.fieldName, tab).append(config.default)}
                                    >
                                      {`Add ${config.label}`}
                                    </Button>
                                  </Stack>
                                ) : null}

                                {fetchFieldArray(config.fieldName, tab).fields?.length > 0 && (
                                  <TotalAssetsCard
                                    summary={{
                                      title: `Total ${config.label}`,
                                      value: calculateTotalAmount(methods.watch(`${tab}.${config.fieldName}`), config.fieldName),
                                    }}
                                  />
                                )}
                              </Fragment>
                            )
                        )}
                      </Fragment>
                    )
                )}
              </Stack>
            </>
          )}

          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter isManagedClient={isManagedClient} from={from} hideNextStep={from === FactFindType.AI} hideSave={isReadOnly} />
              )}
              <SaveDialog open={captureAssetsApi.isPending} />
              <SuccessDialog open={isSuccess.value} />
              <ErrorDialog open={isError.value} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Assets'}
                refetch={refetch}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};
